/* eslint-disable no-console */
function parseGID(gid) {
  return gid.split('/').splice(-1)[0];
}

/** Debounce delay for cart updates to prevent HTTP 409 conflicts */
const CART_UPDATE_DEBOUNCE_MS = 300;

class AjaxService {
  static name = 'ajax';

  constructor() {
    // Pending updates queue for debouncing
    this._pendingUpdates = {};
    this._updateTimeout = null;
    this._updateResolvers = [];
  }

  /**
   * Gets a cart instance from a cart ID
   * @param {string} id cart id
   * @returns formatted cart object
   */
  async get() {
    return fetch(`${window.Shopify.routes.root}cart.js`).then((response) =>
      response.json(),
    );
  }

  async add(_, payload) {
    // add _cart_order property to find last added item
    let cartOrder = 1;
    const cartData = await this.get();
    const { items } = cartData;
    if (cartData.item_count > 0) {
      const maxCartOrder = Math.max(
        ...items.map((item) => item.properties._cart_order),
      );
      cartOrder = maxCartOrder + 1;
    }
    const itemId = parseGID(payload.merchandiseId) || parseGID(payload.id);
    const foundItem = items.find((item) => item.id === +itemId);
    if (foundItem) {
      cartOrder = foundItem.properties._cart_order;
    }
    const payloadArray = Array.isArray(payload) ? payload : [payload];
    const body = {
      items: payloadArray.map(({ merchandiseId, quantity, attributes, id }) => {
        const properties = (attributes || []).reduce(
          (acc, cur) => ({ ...acc, [cur.key]: cur.value }),
          {},
        );
        properties._cart_order = cartOrder;
        return {
          id: parseGID(merchandiseId) || parseGID(id),
          quantity,
          properties,
        };
      }),
    };

    return fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }).then(() => this.get());
  }

  async remove(_, payload) {
    if (!payload) return null;
    const body = {
      updates: Object.fromEntries(payload.map((id) => [id, 0])),
    };
    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }).then((response) => response.json());
  }

  /**
   * Internal method to execute the actual cart update
   * @private
   */
  async _executeCartUpdate() {
    const updates = { ...this._pendingUpdates };
    const resolvers = [...this._updateResolvers];

    // Clear pending state
    this._pendingUpdates = {};
    this._updateResolvers = [];
    this._updateTimeout = null;

    if (Object.keys(updates).length === 0) {
      // Nothing to update, resolve with current cart
      const cart = await this.get();
      resolvers.forEach(({ resolve }) => resolve(cart));
      return;
    }

    try {
      const url = `${window.Shopify.routes.root}cart/update.js`;
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ updates }),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const result = await response.json();
      resolvers.forEach(({ resolve }) => resolve(result));
    } catch (error) {
      console.error('[AjaxService] Cart update failed:', error);
      resolvers.forEach(({ reject }) => reject(error));
    }
  }

  /**
   * Updates cart items with debouncing to prevent HTTP 409 conflicts.
   * Multiple rapid calls are batched into a single request.
   */
  async update(_, payload) {
    if (!payload) return null;

    // For note or attributes updates, execute immediately (no debounce)
    if (payload.note || payload.attributes) {
      const body = payload.note
        ? { note: payload.note }
        : { attributes: payload.attributes };

      return fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }).then((response) => response.json());
    }

    // For quantity updates, use debouncing
    return new Promise((resolve, reject) => {
      // Merge updates into pending queue
      payload.forEach((item) => {
        this._pendingUpdates[item.id] = item.quantity;
      });

      // Add resolver to queue
      this._updateResolvers.push({ resolve, reject });

      // Clear existing timeout
      if (this._updateTimeout) {
        clearTimeout(this._updateTimeout);
      }

      // Schedule debounced update
      this._updateTimeout = setTimeout(() => {
        this._executeCartUpdate();
      }, CART_UPDATE_DEBOUNCE_MS);
    });
  }

  // AJAX Cart only supports updating properties on one line item at a time so payload only accepts an object
  async updateAttributes(_, payload) {
    if (!payload) return null;
    const { attributes, id, quantity } = payload;
    const properties = (attributes || []).reduce(
      (acc, cur) => ({ ...acc, [cur.key]: cur.value }),
      {},
    );
    const body = {
      id: parseGID(id),
    };
    if (quantity) body.quantity = quantity;
    if (attributes) body.properties = properties;
    return fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }).then((response) => response.json());
  }

  async updateCartAttributes(_, payload) {
    if (!payload) return null;
    const response = await fetch(
      `${window.Shopify.routes.root}cart/update.js`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
    );
    if (!response.ok) {
      console.error(
        `[AjaxService] updateCartAttributes failed: ${response.status}`,
      );
      return null;
    }
    return response.json();
  }
}

const AjaxServiceSingleton = new AjaxService();
export default AjaxServiceSingleton;
